import 'react-image-crop/dist/ReactCrop.css';
import * as React from 'react';
import ReactImageCrop, { Crop, ReactCropProps } from 'react-image-crop';

import { Box } from '@botco/library';

type Props = Pick<ReactCropProps, 'circularCrop'> & {
  src: string;
  initialValue?: Partial<Crop>;
  onComplete: (cropImage: string) => void;
};

export const ImageCrop: React.FC<Props> = ({
  src,
  initialValue,
  circularCrop,
  onComplete,
}) => {
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  const [crop, setCrop] = React.useState<Crop>({
    unit: 'px',
    width: 200,
    height: 200,
    x: 100,
    y: 100,
    ...initialValue,
  });

  const handleCropChange = (crop: Crop) => setCrop(crop);

  const handleCropComplete = async (crop: Crop) => {
    if (!imageRef.current) return;

    const croppedImage = getCroppedImg(imageRef.current, crop);
    onComplete(croppedImage ?? '');
  };

  const getCroppedImg = (image: HTMLImageElement, crop: Crop) => {
    if (
      !image ||
      !crop.width ||
      !crop.height ||
      crop.x == null ||
      crop.y == null
    ) {
      return;
    }

    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');

    ctx?.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    const base64Image = canvas.toDataURL('image/jpeg');
    return base64Image;
  };

  return (
    <ReactImageCrop
      crop={crop}
      onChange={handleCropChange}
      onComplete={handleCropComplete}
      circularCrop={circularCrop}
    >
      <Box
        component="img"
        ref={imageRef}
        src={src}
        alt="Source"
        sx={{ width: '100%' }}
      />
    </ReactImageCrop>
  );
};
